import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import Lightbox from 'yet-another-react-lightbox'
import Video from 'yet-another-react-lightbox/plugins/video'
import 'yet-another-react-lightbox/styles.css'
import { Play, Film } from 'lucide-react'
import { Container } from '@/components/layout'
import { SEO } from '@/components/SEO'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import type { GalleryImage, GalleryTag } from '@/types'

export function GalleryPage() {
  const [activeTag, setActiveTag] = useState('all')
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Fetch gallery images from Supabase
  const { data: images, isLoading: imagesLoading } = useQuery({
    queryKey: ['gallery-images'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gallery_images')
        .select(`
          *,
          tags:gallery_image_tags(
            tag:gallery_tags(*)
          )
        `)
        .order('sort_order')
      if (error) throw error
      return data.map((img) => ({
        ...img,
        tags: img.tags?.map((t: { tag: GalleryTag }) => t.tag) || [],
      })) as GalleryImage[]
    },
  })

  // Fetch gallery tags from Supabase
  const { data: tags } = useQuery({
    queryKey: ['gallery-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gallery_tags')
        .select('*')
        .order('name')
      if (error) throw error
      return data as GalleryTag[]
    },
  })

  // Filter images by selected tag
  const filteredImages =
    activeTag === 'all'
      ? images
      : images?.filter((img) => img.tags?.some((tag) => tag.slug === activeTag))

  const openLightbox = (index: number) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  return (
    <>
      <SEO pageKey="gallery" fallback={{ title: 'Marine Equipment Gallery - EngiMarineX Projects & Products' }} />
      {/* Hero */}
      <section className="bg-secondary-900 text-white py-16 lg:py-24">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Gallery</h1>
            <p className="text-lg text-secondary-300 leading-relaxed">
              Browse our collection of product images, installations, and
              manufacturing facilities.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Gallery */}
      <section className="py-16 lg:py-24">
        <Container>
          {/* Tag Filters */}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10">
              <button
                onClick={() => setActiveTag('all')}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium transition-all',
                  activeTag === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                )}
              >
                All
              </button>
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => setActiveTag(tag.slug)}
                  className={cn(
                    'px-4 py-2 rounded-full text-sm font-medium transition-all',
                    activeTag === tag.slug
                      ? 'bg-primary-600 text-white'
                      : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                  )}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          )}

          {/* Loading State */}
          {imagesLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl bg-secondary-200 animate-pulse shadow-md" />
              ))}
            </div>
          )}

          {/* Image Grid */}
          {!imagesLoading && filteredImages && filteredImages.length > 0 && (
            <motion.div
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              <AnimatePresence mode="popLayout">
                {filteredImages.map((image, index) => {
                  const isVideo = image.media_type === 'video'
                  return (
                    <motion.div
                      key={image.id}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      className="group cursor-pointer"
                      onClick={() => openLightbox(index)}
                    >
                      <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-secondary-100 shadow-md">
                        {isVideo ? (
                          <>
                            <video
                              src={image.image_url}
                              className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                              muted
                              playsInline
                            />
                            {/* Video indicator */}
                            <div className="absolute top-3 right-3 p-2 bg-black/70 rounded-lg">
                              <Film className="w-4 h-4 text-white" />
                            </div>
                            {/* Play button */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-16 h-16 bg-black/50 rounded-full flex items-center justify-center group-hover:bg-primary-600 transition-colors">
                                <Play className="w-8 h-8 text-white ml-1" fill="white" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <img
                            src={image.image_url}
                            alt={image.title || 'Gallery image'}
                            loading="lazy"
                            className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                          />
                        )}
                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                        {/* Title & Description overlay */}
                        {(image.title || image.description) && (
                          <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                            {image.title && (
                              <h3 className="text-white font-semibold text-lg leading-tight">
                                {image.title}
                              </h3>
                            )}
                            {image.description && (
                              <p className="text-white/80 text-sm mt-1 line-clamp-2">
                                {image.description}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Tags */}
                        {image.tags && image.tags.length > 0 && (
                          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                            {image.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag.id}
                                className="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-secondary-700 text-xs font-medium rounded-full"
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )
                })}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Empty State */}
          {!imagesLoading && (!filteredImages || filteredImages.length === 0) && (
            <div className="text-center py-16">
              <p className="text-secondary-500">No images found for this filter.</p>
            </div>
          )}
        </Container>
      </section>

      {/* Lightbox */}
      {filteredImages && filteredImages.length > 0 && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          plugins={[Video]}
          slides={filteredImages.map((img) => {
            if (img.media_type === 'video') {
              return {
                type: 'video' as const,
                sources: [{ src: img.image_url, type: 'video/mp4' }],
                poster: img.thumbnail_url || undefined,
              }
            }
            return {
              src: img.image_url,
              alt: img.title || 'Gallery image',
            }
          })}
        />
      )}
    </>
  )
}
